import type { ReleasePolicy } from '@gonfalon/openapi/types';

/**
 * Mirrors the backend's `GetReleasePolicyForFlagRelease` resolution, but
 * without flag-tag filtering (the new flag has no tags at composer time).
 *
 * Resolution order:
 * 1. Filter to policies that have no flagTagKeys scope (would apply to all flags).
 * 2. Among those, keep policies that either have no environmentKeys scope (default)
 *    or explicitly list the target environment.
 * 3. Sort non-default policies by ascending rank, then append the default policy last.
 * 4. Return the first match, or undefined if none apply.
 */
export function getApplicableReleasePolicyForEnvironment(
  policies: ReleasePolicy[],
  environmentKey: string,
): ReleasePolicy | undefined {
  const candidates = policies.filter((policy) => {
    const scope = policy.scope;
    // Policies with flagTagKeys are tag-scoped; since the new flag has no tags,
    // skip them (the backend would also skip them at release time unless the flag
    // later has matching tags).
    if (scope?.flagTagKeys && scope.flagTagKeys.length > 0) {
      return false;
    }
    // A policy with no environmentKeys (or no scope at all) applies to all envs.
    if (!scope?.environmentKeys || scope.environmentKeys.length === 0) {
      return true;
    }
    return scope.environmentKeys.includes(environmentKey);
  });

  if (candidates.length === 0) {
    return undefined;
  }

  // A policy is "default" when it has no scope or an empty scope.
  const isDefault = (p: ReleasePolicy) => {
    const scope = p.scope;
    if (!scope) {
      return true;
    }
    const hasEnvKeys = scope.environmentKeys && scope.environmentKeys.length > 0;
    const hasTagKeys = scope.flagTagKeys && scope.flagTagKeys.length > 0;
    return !hasEnvKeys && !hasTagKeys;
  };

  const nonDefault = candidates.filter((p) => !isDefault(p)).sort((a, b) => a.rank - b.rank);
  const defaultPolicies = candidates.filter(isDefault);

  return [...nonDefault, ...defaultPolicies][0];
}
